/**
 * Stripe money amounts are integer minor units (e.g. `1999` = $19.99 for a
 * 2-decimal currency), but the exponent is NOT always 2 — Stripe documents a
 * "zero-decimal" currency list (JPY, KRW, …) whose integer amount IS the
 * major-unit amount, and a "three-decimal" list (BHD, …). Dividing every
 * amount by 100 (legacy `PaymentInvoiceListComponent.getTotal`, ported
 * verbatim from `Misc.formatNumberN((value || 0) / 100, 2)`) is wrong for
 * both: it shrinks a JPY invoice 100x and misplaces a BHD invoice's decimal.
 *
 * This only applies to STRIPE-sourced amounts (`Invoice.total`,
 * `CheckoutSession.amount_total`) — `api.feesubscription.com`'s own
 * `*Formatted` fields (`monthlyFeeFormatted`, `priceFormatted`,
 * `amountFormatted`) are pre-formatted server-side strings, already correct,
 * and must be rendered as-is; `Plan.price`/`Product.price` are the payment
 * service's own catalogue prices in MAJOR units (verified against
 * `api.feesubscription.com` `plan_export`/`product_export` — no `/100`
 * anywhere in that path, fixture `{"price": 19.99, ...}`), not Stripe minor
 * units, so they must NOT be divided either.
 *
 * Source: https://docs.stripe.com/currencies#zero-decimal and
 * https://docs.stripe.com/currencies#three-decimal (2026-09-27).
 */
const ZERO_DECIMAL_CURRENCIES = new Set([
  'bif',
  'clp',
  'djf',
  'gnf',
  'jpy',
  'kmf',
  'krw',
  'mga',
  'pyg',
  'rwf',
  'ugx',
  'vnd',
  'vuv',
  'xaf',
  'xof',
  'xpf',
])

const THREE_DECIMAL_CURRENCIES = new Set(['bhd', 'jod', 'kwd', 'omr', 'tnd'])

/** How many digits of `amount` are the fractional part, for this ISO 4217 /
 * Stripe currency code. Unknown/missing currency defaults to 2 (the common
 * case, and legacy's only case). */
export function currencyExponent(currency: string | null | undefined): number {
  const code = (currency ?? '').trim().toLowerCase()
  if (ZERO_DECIMAL_CURRENCIES.has(code)) return 0
  if (THREE_DECIMAL_CURRENCIES.has(code)) return 3
  return 2
}

/**
 * Formats a Stripe minor-unit integer amount as a decimal string, honoring
 * the currency's exponent (never a flat `/ 100`). The decimal string is
 * built directly from the integer's digits (padding + a slice for the
 * decimal point) rather than by dividing the minor-unit integer — the
 * `Number()` parse only happens on that already-correct decimal string, and
 * only to hand it to `Intl.NumberFormat` for locale thousands-grouping, so
 * no float division ever touches the raw Stripe integer.
 */
export function formatMinorUnits(amountMinor: number | null | undefined, currency: string | null | undefined): string {
  const exponent = currencyExponent(currency)
  const value = Number.isFinite(amountMinor) ? Math.trunc(amountMinor as number) : 0
  const negative = value < 0
  const digits = Math.abs(value).toString().padStart(exponent + 1, '0')
  const integerDigits = exponent === 0 ? digits : digits.slice(0, -exponent)
  const fractionDigits = exponent === 0 ? '' : digits.slice(-exponent)
  const decimalString = `${negative ? '-' : ''}${integerDigits}${fractionDigits ? `.${fractionDigits}` : ''}`

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: exponent,
    maximumFractionDigits: exponent,
  }).format(Number(decimalString))
}
